// /app/frontend/src/components/AdminUsersSection.jsx
//
// Admin Panel — Account Management section.
//
// Shows ALL registered Syllox users (existing + future), searchable
// by email/name/username, paginated 20 per page. Each row exposes a
// permanent-delete action that cascades the target user's owned data
// across every collection in the data model (subjects, chapters, study
// sessions, targets, mock tests, posts, comments, friendships, messages,
// AI data, payments, sessions, etc.) — same cascade as the user's own
// Settings → Danger Zone delete.
//
// Security:
//   • Every request runs through the backend's `require_admin` gate.
//   • Delete button is disabled for admin accounts and for the caller
//     themselves (server also refuses these — this is a UX safeguard).
//   • `window.confirm` prompts with the target's identity so an admin
//     can't fat-finger the wrong row.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Trash2, Loader2, ChevronLeft, ChevronRight, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminUsers, adminDeleteUser } from "../lib/adminApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 20;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

const initial = (s) => (s?.trim?.()?.[0] || "?").toUpperCase();

export default function AdminUsersSection({ onUserDeleted }) {
  const { user: me } = useAuth();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const debounceRef = useRef(null);

  // Debounce the search field so we don't hammer Mongo on every keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebounced(query);
      setSkip(0); // any query change resets to page 1
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminUsers({ q: debounced, skip, limit: PAGE_SIZE });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, [debounced, skip]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (u) => {
    const label = u.name || u.username || u.email || u.user_id;
    if (!window.confirm(
      `Permanently delete "${label}" and ALL their data?\n\n` +
      `This removes their profile, subjects, chapters, study sessions, ` +
      `mock tests, community posts and comments, friendships, messages, ` +
      `AI conversations, payment records, and login history.\n\n` +
      `This cannot be undone.`
    )) return;
    setDeletingId(u.user_id);
    try {
      const res = await adminDeleteUser(u.user_id);
      const removed = res?.deleted?.users || 0;
      toast.success(
        removed
          ? `Deleted ${label} and ${Object.values(res.deleted || {}).reduce((a, b) => a + (b || 0), 0)} owned records.`
          : `Deleted ${label}.`
      );
      // Optimistically drop the row + refresh so `total` and page stay honest.
      setItems((prev) => prev.filter((x) => x.user_id !== u.user_id));
      setTotal((t) => Math.max(t - 1, 0));
      onUserDeleted?.();
      // If the current page emptied out, drop back a page.
      if (items.length <= 1 && skip >= PAGE_SIZE) {
        setSkip((s) => Math.max(s - PAGE_SIZE, 0));
      } else {
        // Otherwise refill so pagination stays contiguous.
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete user.");
    } finally {
      setDeletingId(null);
    }
  };

  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = skip > 0 && !loading;
  const canNext = skip + PAGE_SIZE < total && !loading;

  const range = useMemo(() => {
    if (total === 0) return "0 accounts";
    const from = skip + 1;
    const to = Math.min(skip + items.length, total);
    return `${from}–${to} of ${total.toLocaleString()}`;
  }, [skip, items.length, total]);

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
      data-testid="admin-users-section"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-emerald-600" />
          <div>
            <h2 className="font-heading text-lg font-semibold">All Accounts</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Search, review, and permanently delete any Syllox account.
            </p>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email, name, or username…"
            className="h-9 w-64 pl-8 text-sm"
            data-testid="admin-users-search"
          />
        </div>
      </div>

      {error && (
        <div className="border-b border-red-500/30 bg-red-500/10 px-5 py-2 text-sm text-red-700 dark:text-red-300" data-testid="admin-users-error">
          {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-500">
          <Loader2 size={14} className="animate-spin" /> Loading accounts…
        </div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-zinc-500" data-testid="admin-users-empty">
          No accounts match your search.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/50">
                <tr>
                  <th className="px-5 py-2.5 font-medium">User</th>
                  <th className="px-5 py-2.5 font-medium">Email</th>
                  <th className="px-5 py-2.5 font-medium">Provider</th>
                  <th className="px-5 py-2.5 font-medium">Plan</th>
                  <th className="px-5 py-2.5 font-medium">Joined</th>
                  <th className="px-5 py-2.5 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {items.map((u) => {
                  const isSelf = me?.user_id === u.user_id;
                  const disabled = u.is_admin || isSelf || deletingId === u.user_id;
                  return (
                    <tr
                      key={u.user_id}
                      data-testid={`admin-account-row-${u.user_id}`}
                      className="hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                              {initial(u.name || u.username)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <div className="truncate font-medium text-zinc-900 dark:text-zinc-100">{u.name || "—"}</div>
                              {u.is_admin && (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                                  <ShieldAlert size={10}/> Admin
                                </span>
                              )}
                            </div>
                            <div className="truncate text-xs text-zinc-500">@{u.username || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-zinc-700 dark:text-zinc-300">{u.email || "—"}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-zinc-500/10 px-2 py-0.5 text-xs capitalize text-zinc-600 dark:text-zinc-400">
                          {u.auth_provider || "email"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {u.is_premium ? (
                          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                            Premium
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">Free</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-zinc-500">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={disabled}
                          onClick={() => handleDelete(u)}
                          data-testid={`admin-delete-user-${u.user_id}`}
                          className="text-red-600 hover:bg-red-500/10 hover:text-red-700 disabled:opacity-40"
                          title={u.is_admin ? "Admin accounts cannot be deleted" : isSelf ? "Use Settings to delete your own account" : "Permanently delete this account"}
                        >
                          {deletingId === u.user_id ? (
                            <Loader2 size={14} className="animate-spin"/>
                          ) : (
                            <><Trash2 size={14} className="mr-1"/> Delete</>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="divide-y divide-zinc-100 md:hidden dark:divide-zinc-800">
            {items.map((u) => {
              const isSelf = me?.user_id === u.user_id;
              const disabled = u.is_admin || isSelf || deletingId === u.user_id;
              return (
                <div
                  key={u.user_id}
                  className="flex items-start gap-3 px-5 py-3"
                  data-testid={`admin-account-card-${u.user_id}`}
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  ) : (
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white">
                      {initial(u.name || u.username)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="truncate font-medium">{u.name || "—"}</div>
                      {u.is_admin && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                          <ShieldAlert size={10}/> Admin
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-zinc-500">{u.email || "—"}</div>
                    <div className="mt-1 text-[11px] text-zinc-400">
                      {u.auth_provider || "email"} · joined {fmtDate(u.created_at)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => handleDelete(u)}
                    data-testid={`admin-delete-user-${u.user_id}`}
                    className="shrink-0 text-red-600 hover:bg-red-500/10 hover:text-red-700 disabled:opacity-40"
                  >
                    {deletingId === u.user_id ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Footer / pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 px-5 py-3 text-xs text-zinc-500 dark:border-zinc-800">
        <div data-testid="admin-users-range">{range}</div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!canPrev}
            onClick={() => setSkip((s) => Math.max(s - PAGE_SIZE, 0))}
            data-testid="admin-users-prev"
          >
            <ChevronLeft size={14}/> Prev
          </Button>
          <span className="tabular-nums" data-testid="admin-users-page">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={!canNext}
            onClick={() => setSkip((s) => s + PAGE_SIZE)}
            data-testid="admin-users-next"
          >
            Next <ChevronRight size={14}/>
          </Button>
        </div>
      </div>
    </div>
  );
}
